/**
 * usePendingPayments Hook
 *
 * Fetches pending payments awaiting approval/rejection.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const PendingPaymentsDocument = `
  query PendingPayments {
    pendingPayments {
      id
      status
      totalAmount
      currency
      createdAt
      event {
        id
        title
        startsAt
      }
      customer {
        id
        firstName
        lastName
        username
      }
      items {
        quantity
        ticketType {
          name
        }
      }
      receiptUrl
      receiptUploadedAt
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface PendingPaymentEvent {
  id: string;
  title: string;
  startsAt: string;
}

export interface PendingPaymentCustomer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

export interface PendingPaymentItem {
  quantity: number;
  ticketType: {
    name: string;
  };
}

export interface PendingPayment {
  id: string;
  status: string;
  totalAmount: string;
  currency: string;
  createdAt: string;
  event: PendingPaymentEvent;
  customer: PendingPaymentCustomer;
  items: PendingPaymentItem[];
  receiptUrl: string | null;
  receiptUploadedAt: string | null;
}

interface UsePendingPaymentsOptions {
  pause?: boolean;
}

interface UsePendingPaymentsResult {
  payments: PendingPayment[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch pending payments that need approval.
 */
export function usePendingPayments(
  options: UsePendingPaymentsOptions = {}
): UsePendingPaymentsResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    pendingPayments: PendingPayment[];
  }>({
    query: PendingPaymentsDocument,
    pause,
  });

  return {
    payments: result.data?.pendingPayments ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
